import { computeAnnualVolumeM3 } from '../../../../../../utils/consumptionCalculations.js';

const MAX_SLICES = 6;

/**
 * Répartition de la consommation totale du bâtiment par type_usage (Douche, Sanitaire,
 * Cuisine…), pour donner le contexte du gisement/besoin REUT au sein de la consommation
 * globale — reprend le principe du diagramme de référence (donut + libellés de catégories).
 * Chaque catégorie est coloriée selon son rôle dans le module REUT (pas son volume) :
 * "gisement" si elle contient au moins un item source coché à l'étape 1, "besoin" si elle
 * contient au moins un item débouché coché à l'étape 2, "neutre" sinon — déterminé via
 * usageId, indépendamment du volume affiché (qui reste celui de TOUTE la catégorie, cochée
 * ou non, pour représenter fidèlement la consommation réelle du bâtiment).
 * @returns {Object[]} { name, volume, role: 'gisement'|'besoin'|'neutre' }, ≤ 6 tranches
 *   (les plus petites catégories neutres sont repliées dans "Autres").
 */
export const computeCategoryBreakdown = (equipments, usages, sources, targets) => {
  const equipmentById = new Map(equipments.map(e => [e._id, e]));
  const gisementUsageIds = new Set(sources.filter(s => s.enabled && s.usageId).map(s => s.usageId));
  const besoinUsageIds = new Set(targets.filter(t => t.enabled && t.usageId).map(t => t.usageId));

  const byCategory = new Map();
  usages.forEach(usage => {
    const name = usage.type_usage || 'Autres';
    const equipment = equipmentById.get(usage._id_equipement);
    const volume = computeAnnualVolumeM3(equipment, usage);

    const entry = byCategory.get(name) || { name, volume: 0, hasGisement: false, hasBesoin: false };
    entry.volume += volume;
    if (gisementUsageIds.has(usage._id)) entry.hasGisement = true;
    if (besoinUsageIds.has(usage._id)) entry.hasBesoin = true;
    byCategory.set(name, entry);
  });

  const categories = Array.from(byCategory.values())
    .filter(c => c.volume > 0)
    .map(c => ({
      name: c.name,
      volume: Math.round(c.volume * 100) / 100,
      role: c.hasGisement ? 'gisement' : c.hasBesoin ? 'besoin' : 'neutre',
    }));

  const featured = categories.filter(c => c.role !== 'neutre');
  const neutral = categories.filter(c => c.role === 'neutre').sort((a, b) => b.volume - a.volume);

  const slotsLeft = Math.max(0, MAX_SLICES - featured.length);
  const kept = neutral.slice(0, slotsLeft);
  const folded = neutral.slice(slotsLeft);

  const result = [...featured, ...kept];
  if (folded.length > 0) {
    result.push({ name: 'Autres', volume: folded.reduce((sum, c) => sum + c.volume, 0), role: 'neutre' });
  }

  return result.sort((a, b) => b.volume - a.volume);
};
